import {
  PublicKey,
  ConfirmOptions,
  Keypair,
  Connection,
  Transaction,
  SYSVAR_INSTRUCTIONS_PUBKEY,
  TransactionInstruction,
} from '@solana/web3.js'
import { Program, Provider } from '@project-serum/anchor'
import { createApproveInstruction, createRevokeInstruction, EditionMetadata } from './types'
import { DataV2, Metadata } from '@metaplex-foundation/mpl-token-metadata'
import { PROGRAM_ID as METADATA_PROGRAM_ID } from '@metaplex-foundation/mpl-token-metadata'
import idl from './gpl_token.json'
import { AuctionHouseProgram } from '@metaplex-foundation/mpl-auction-house'
import { NATIVE_MINT } from '@solana/spl-token'
import { txSigner } from '../data/TxSigner'
import { Base64 } from 'js-base64'
import { ListingReceipt, Query_Root } from '../graphql/indexer-graphql-types'
import { indexerClient, LISTING_STATUS_QUERY } from '../graphql/graphql-queries'
// import adminKey from './test-admin-keypair.json'

export class TokenProgram {
  static readonly PUBKEY = new PublicKey(
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
      ? 'GLASSmeQYntNce8npQvG4URFvmvDcz7KCvxLJfFNPBKA'
      : 'AyYHnTAXLFDQPePLietBP2PU97QhjeqWSnJ4APi1JP7r'
  )

  // TODO: decide if separate authority accounts are needed or if they
  // can just be a single authority.
  static readonly PLATFORM_SETTINGS_PREFIX = 'platform_settings'
  static readonly MINT_AUTHORITY_PREFIX = 'mint_authority'
  static readonly METADATA_AUTHORITY_PREFIX = 'metadata_authority'
  static readonly EDITION_METADATA_PREFIX = 'edition_metadata'
  static readonly PRIMARY_SALE_RECEIPT_PREFIX = 'primary_sale_receipt'

  static readonly TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
  static readonly SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID = new PublicKey(
    'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'
  )

  static readonly METADATA_PREFIX = 'metadata'
  static readonly TOKEN_METADATA_PROGRAM_ID = new PublicKey(
    'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s'
  )

  static readonly confirmOptions: ConfirmOptions = {
    skipPreflight: false,
    commitment: 'confirmed',
    preflightCommitment: 'processed',
    maxRetries: 10,
  }

  static getProgram(provider: Provider): Program {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return new Program(idl, this.PUBKEY, provider)
  }

  static async getPlatformTreasuryAddress(program: Program): Promise<PublicKey> {
    const [platformSettings] = await TokenProgram.findPlatformSettingsAddress()
    const platformSettingsAccount = await program.account.platformSettings.fetch(platformSettings)
    return platformSettingsAccount.platformTreasury as PublicKey
  }

  static async getPlatformSignerAddress(program: Program): Promise<PublicKey> {
    const [platformSettings] = await TokenProgram.findPlatformSettingsAddress()
    const platformSettingsAccount = await program.account.platformSettings.fetch(platformSettings)
    return platformSettingsAccount.platformSigner as PublicKey
  }

  static async createEditionMetadata(
    wallet: PublicKey,
    provider: Provider,
    program: Program,
    editionMetadata: EditionMetadata,
    metadataData: DataV2,
    isMutable: boolean,
    mint: Keypair,
    platformTreasury: PublicKey,
    platformSigner: PublicKey
  ): Promise<{ buffer: Buffer; fee: number }> {
    const [[metadataAuthority], [metadata]] = await Promise.all([
      TokenProgram.findMetadataAuthorityAddress(),
      TokenProgram.findMetadataAddress(mint.publicKey),
    ])

    editionMetadata.metadata = metadata

    metadataData.creators?.push({
      address: metadataAuthority,
      verified: false,
      share: 0,
    })

    const transaction = new Transaction({ feePayer: wallet })
    transaction.add(
      await program.methods
        .createEditionMetadata(editionMetadata, metadataData, isMutable)
        .accounts({
          platformSigner: platformSigner,
          mint: mint.publicKey,
          metadata: metadata,
          platformTreasury: platformTreasury,
          metadataProgram: METADATA_PROGRAM_ID,
        })
        .instruction()
    )

    transaction.recentBlockhash = (await provider.connection.getLatestBlockhash()).blockhash

    transaction.partialSign(mint)

    const encoded = Base64.fromUint8Array(
      transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
      false
    )
    const adminSignedIx = await txSigner(encoded)

    const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

    const signed = await provider.wallet.signTransaction(tx)

    const serialized = signed.serialize()

    const fee = await tx.getEstimatedFee(provider.connection)

    return { buffer: serialized, fee: fee + Number(editionMetadata.platformCreateFeeLamports) }
  }

  /**
   * Creates sell offer and listing receipt
   *
   * @param connection   Connection to use
   * @param seller        Payer
   * @param auctionHouse Auction house
   * @param salePrice    Sale price for tokenSize tokens
   * @param tokenSize    Number of tokens to offer for sale
   *
   * @return Transaction
   */
  static async createSellOffer(
    connection: Connection,
    seller: PublicKey,
    auctionHouse: PublicKey,
    authority: PublicKey,
    mint: PublicKey,
    metadata: PublicKey,
    salePrice: number,
    tokenSize: number
  ): Promise<Transaction> {
    const result: Query_Root = (
      await indexerClient.query({
        query: LISTING_STATUS_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
          address: seller.toString(),
          mint: mint.toString(),
        },
      })
    ).data

    let amount = 0
    // let delegatedAmount = 0
    let oldestListing: ListingReceipt | undefined = undefined
    let delegate: PublicKey | undefined = undefined
    let numListings = 0

    const tokenAcct =
      result.tokenAccount && result.tokenAccount.length > 0 ? result.tokenAccount[0] : undefined
    if (tokenAcct) {
      amount = tokenAcct.amount
      numListings =
        tokenAcct.mintObject?.editionMetadata?.metadataObject?.listingReceiptsAggregate.aggregate
          ?.count || 0
      // delegatedAmount = tokenAcct.delegatedAmount
      oldestListing =
        tokenAcct.mintObject?.editionMetadata?.metadataObject?.listingReceipts &&
        tokenAcct.mintObject?.editionMetadata?.metadataObject?.listingReceipts.length > 0
          ? tokenAcct.mintObject?.editionMetadata?.metadataObject?.listingReceipts[0]
          : undefined
      delegate = tokenAcct.delegate ? new PublicKey(tokenAcct.delegate) : undefined
    }

    const newDelegatedAmount = Math.min(amount, numListings)

    const [[tokenAccount], [auctionHouseFeeAccount], [programAsSigner, programAsSignerBump]] =
      await Promise.all([
        TokenProgram.findAssociatedTokenAccountAddress(mint, seller),
        AuctionHouseProgram.findAuctionHouseFeeAddress(auctionHouse),
        AuctionHouseProgram.findAuctionHouseProgramAsSignerAddress(),
      ])

    const [[sellerTradeState, tradeStateBump], [freeSellerTradeState, freeTradeStateBump]] =
      await Promise.all([
        AuctionHouseProgram.findTradeStateAddress(
          seller,
          auctionHouse,
          tokenAccount,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        ),
        AuctionHouseProgram.findTradeStateAddress(
          seller,
          auctionHouse,
          tokenAccount,
          NATIVE_MINT,
          mint,
          0,
          tokenSize
        ),
      ])

    const [listingReceipt, receiptBump] = await AuctionHouseProgram.findListingReceiptAddress(
      sellerTradeState
    )

    const accounts = {
      wallet: seller,
      tokenAccount,
      metadata,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
      sellerTradeState,
      freeSellerTradeState,
      programAsSigner,
    }

    const args = {
      tradeStateBump,
      freeTradeStateBump,
      programAsSignerBump,
      buyerPrice: salePrice,
      tokenSize,
    }

    const transaction = new Transaction({ feePayer: seller })

    if (numListings >= amount && oldestListing) {
      // cancel oldest listing
      const cancelInstructions = await this.createCancelSellOfferInstructions(
        seller,
        auctionHouse,
        authority,
        mint,
        oldestListing.price,
        tokenSize
      )
      cancelInstructions.forEach((instruction) => transaction.add(instruction))
      console.log('CANCELLING PREVIOUS LISTING')
    }

    const sellIx = await AuctionHouseProgram.instructions.createSellInstruction(accounts, args)
    // sellIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
    transaction.add(sellIx)

    const listingReceiptAccounts = {
      receipt: listingReceipt,
      bookkeeper: seller,
      instruction: SYSVAR_INSTRUCTIONS_PUBKEY,
    }

    const listingReceiptArgs = {
      receiptBump,
    }

    transaction.add(
      AuctionHouseProgram.instructions.createPrintListingReceiptInstruction(
        listingReceiptAccounts,
        listingReceiptArgs
      )
    )

    if (delegate) {
      // approve instruction for delegated amount + 1
      const approveInstruction = createApproveInstruction(
        tokenAccount,
        delegate,
        seller,
        numListings < amount ? newDelegatedAmount + 1 : newDelegatedAmount
      )
      transaction.add(approveInstruction)
      console.log(
        `APPROVING FOR DELEGATED AMOUNT ${
          numListings < amount ? newDelegatedAmount + 1 : newDelegatedAmount
        }`
      )
    }

    transaction.recentBlockhash = (await connection.getLatestBlockhash()).blockhash

    return transaction
  }

  /**
   * Creates buy offer and bid receipt
   *
   * @param connection   Anchor program to use
   * @param buyer        Buyer
   * @param tokenAccount token account of seller
   * @param auctionHouse Auction house
   * @param authority    Auction house authority
   * @param mint         Semi-fungible mint
   * @param metadata     Semi-fungible metadata
   * @param salePrice    Sale price for tokenSize tokens
   * @param tokenSize    Number of tokens to offer for sale
   * @param publicBuy    true for public, false for private
   *
   * @return Transaction
   */
  static async createBuyOffer(
    connection: Connection,
    buyer: PublicKey,
    tokenAccount: PublicKey,
    auctionHouse: PublicKey,
    authority: PublicKey,
    mint: PublicKey,
    metadata: PublicKey,
    salePrice: number,
    tokenSize: number,
    publicBuy: boolean
  ): Promise<Transaction> {
    const [auctionHouseFeeAccount] = await AuctionHouseProgram.findAuctionHouseFeeAddress(
      auctionHouse
    )

    console.log(tokenAccount.toString())

    const tradeStateProm = publicBuy
      ? AuctionHouseProgram.findPublicBidTradeStateAddress(
          buyer,
          auctionHouse,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        )
      : AuctionHouseProgram.findTradeStateAddress(
          buyer,
          auctionHouse,
          tokenAccount,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        )

    const [[buyerTradeState, tradeStateBump], [escrowPaymentAccount, escrowPaymentBump]] =
      await Promise.all([
        tradeStateProm,
        AuctionHouseProgram.findEscrowPaymentAccountAddress(auctionHouse, buyer),
      ])

    const [bidReceipt, receiptBump] = await AuctionHouseProgram.findBidReceiptAddress(
      buyerTradeState
    )

    const accounts = {
      wallet: buyer,
      paymentAccount: buyer,
      transferAuthority: buyer,
      treasuryMint: NATIVE_MINT,
      tokenAccount,
      metadata,
      escrowPaymentAccount,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
      buyerTradeState,
    }

    const args = {
      tradeStateBump,
      escrowPaymentBump,
      buyerPrice: salePrice,
      tokenSize,
    }

    const depositAccounts = {
      wallet: buyer,
      paymentAccount: buyer,
      transferAuthority: buyer,
      escrowPaymentAccount,
      treasuryMint: NATIVE_MINT,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
    }

    const depositArgs = {
      escrowPaymentBump,
      amount: salePrice,
    }

    const transaction = new Transaction({ feePayer: buyer })
    const depositIx = AuctionHouseProgram.instructions.createDepositInstruction(
      depositAccounts,
      depositArgs
    )
    // depositIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
    transaction.add(depositIx)

    if (publicBuy) {
      const buyIx = AuctionHouseProgram.instructions.createPublicBuyInstruction(accounts, args)
      // buyIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
      transaction.add(buyIx)
    } else {
      const buyIx = AuctionHouseProgram.instructions.createBuyInstruction(accounts, args)
      // buyIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
      transaction.add(buyIx)
    }

    const bidReceiptAccounts = {
      receipt: bidReceipt,
      bookkeeper: buyer,
      instruction: SYSVAR_INSTRUCTIONS_PUBKEY,
    }

    const bidReceiptArgs = {
      receiptBump,
    }

    transaction.add(
      AuctionHouseProgram.instructions.createPrintBidReceiptInstruction(
        bidReceiptAccounts,
        bidReceiptArgs
      )
    )

    transaction.recentBlockhash = (await connection.getLatestBlockhash()).blockhash

    return transaction
  }

  /**
   * Executes sale
   *
   * @param fullTx       transaction to add execution to (optional)
   * @param provider     Anchor provider
   * @param buyer        Buyer
   * @param seller       Seller
   * @param auctionHouse Auction house
   * @param authority    Auction house authority
   * @param mint         Semi-fungible mint
   * @param metadata     Semi-fungible metadata
   * @param salePrice    Sale price for tokenSize tokens
   * @param tokenSize    Number of tokens to offer for sale
   *
   * @return Tx hash
   */
  static async executeSale(
    provider: Provider,
    buyer: PublicKey,
    seller: PublicKey,
    auctionHouse: PublicKey,
    authority: PublicKey,
    mint: PublicKey,
    metadata: PublicKey,
    salePrice: number,
    tokenSize: number,
    publicBuy = false,
    wallet: PublicKey,
    fullTx?: Transaction
  ): Promise<Buffer> {
    const [
      [buyerTokenAccount],
      [sellerTokenAccount],
      [auctionHouseFeeAccount],
      [auctionHouseTreasury],
      [programAsSigner, programAsSignerBump],
    ] = await Promise.all([
      TokenProgram.findAssociatedTokenAccountAddress(mint, buyer),
      TokenProgram.findAssociatedTokenAccountAddress(mint, seller),
      AuctionHouseProgram.findAuctionHouseFeeAddress(auctionHouse),
      AuctionHouseProgram.findAuctionHouseTreasuryAddress(auctionHouse),
      AuctionHouseProgram.findAuctionHouseProgramAsSignerAddress(),
    ])

    const tradeStateProm = publicBuy
      ? AuctionHouseProgram.findPublicBidTradeStateAddress(
          buyer,
          auctionHouse,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        )
      : AuctionHouseProgram.findTradeStateAddress(
          buyer,
          auctionHouse,
          sellerTokenAccount,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        )

    const [
      [buyerTradeState],
      [sellerTradeState],
      [freeTradeState, freeTradeStateBump],
      [escrowPaymentAccount, escrowPaymentBump],
    ] = await Promise.all([
      tradeStateProm,
      AuctionHouseProgram.findTradeStateAddress(
        seller,
        auctionHouse,
        sellerTokenAccount,
        NATIVE_MINT,
        mint,
        salePrice,
        tokenSize
      ),
      AuctionHouseProgram.findTradeStateAddress(
        seller,
        auctionHouse,
        sellerTokenAccount,
        NATIVE_MINT,
        mint,
        0,
        tokenSize
      ),
      AuctionHouseProgram.findEscrowPaymentAccountAddress(auctionHouse, buyer),
    ])

    const [[bidReceipt], [listingReceipt], [purchaseReceipt, purchaseReceiptBump]] =
      await Promise.all([
        AuctionHouseProgram.findBidReceiptAddress(buyerTradeState),
        AuctionHouseProgram.findListingReceiptAddress(sellerTradeState),
        AuctionHouseProgram.findPurchaseReceiptAddress(sellerTradeState, buyerTradeState),
      ])

    const metaDataInfo = await Metadata.fromAccountAddress(provider.connection, metadata)

    const accounts = {
      buyer: buyer,
      seller,
      tokenAccount: sellerTokenAccount,
      tokenMint: mint,
      metadata: metadata,
      treasuryMint: NATIVE_MINT,
      escrowPaymentAccount: escrowPaymentAccount,
      sellerPaymentReceiptAccount: seller,
      buyerReceiptTokenAccount: buyerTokenAccount,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
      auctionHouseTreasury,
      buyerTradeState,
      sellerTradeState,
      freeTradeState,
      programAsSigner,
    }

    const args = {
      escrowPaymentBump,
      freeTradeStateBump,
      programAsSignerBump,
      buyerPrice: salePrice,
      tokenSize,
    }

    const transaction = fullTx ? fullTx : new Transaction({ feePayer: wallet })

    // let delegate: PublicKey | undefined = undefined
    let delegatedAmount: number | undefined = undefined

    if (wallet.toString() === buyer.toString()) {
      const result: Query_Root = (
        await indexerClient.query({
          query: LISTING_STATUS_QUERY,
          fetchPolicy: 'no-cache',
          variables: {
            address: buyer.toString(),
            mint: mint.toString(),
          },
        })
      ).data

      const tokenAcct =
        result.tokenAccount && result.tokenAccount.length > 0 ? result.tokenAccount[0] : undefined

      if (tokenAcct) {
        // delegate = tokenAcct.delegate ? new PublicKey(tokenAcct.delegate) : undefined
        delegatedAmount = tokenAcct.delegatedAmount
      }
    }

    // if (delegate) {
    if (delegatedAmount && delegatedAmount > 0 && wallet.toString() === buyer.toString()) {
      const revokeInstruction = createRevokeInstruction(buyerTokenAccount, buyer)
      transaction.add(revokeInstruction)
    } else if (delegatedAmount && delegatedAmount > 0 && wallet.toString() === seller.toString()) {
      const revokeInstruction = createRevokeInstruction(sellerTokenAccount, seller)
      transaction.add(revokeInstruction)
    }
    // }

    const instruction = AuctionHouseProgram.instructions.createExecuteSaleInstruction(
      accounts,
      args
    )

    metaDataInfo.data.creators?.forEach((c) => {
      instruction.keys.push({
        pubkey: c.address,
        isWritable: true,
        isSigner: false,
      })
    })

    // instruction.keys.push({ pubkey: authority, isSigner: true, isWritable: false })

    transaction.add(instruction)

    const purchaseReceiptAccounts = {
      purchaseReceipt,
      listingReceipt,
      bidReceipt,
      bookkeeper: wallet,
      instruction: SYSVAR_INSTRUCTIONS_PUBKEY,
    }

    const purchaseReceiptArgs = {
      purchaseReceiptBump,
    }

    transaction.add(
      AuctionHouseProgram.instructions.createPrintPurchaseReceiptInstruction(
        purchaseReceiptAccounts,
        purchaseReceiptArgs
      )
    )

    if (programAsSigner && delegatedAmount && wallet.toString() === buyer.toString()) {
      const delegateInstruction = createApproveInstruction(
        buyerTokenAccount,
        programAsSigner,
        buyer,
        delegatedAmount
      )
      transaction.add(delegateInstruction)
    } else if (programAsSigner && delegatedAmount && wallet.toString() === seller.toString()) {
      const delegateInstruction = createApproveInstruction(
        sellerTokenAccount,
        programAsSigner,
        seller,
        delegatedAmount
      )
      transaction.add(delegateInstruction)
    }

    transaction.recentBlockhash = (await provider.connection.getLatestBlockhash()).blockhash

    // const encoded = Base64.fromUint8Array(
    //   transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
    //   false
    // )
    // const adminSignedIx = await txSigner(encoded) // TODO: secondaryTxSigner

    // const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

    const signed = await provider.wallet.signTransaction(transaction)

    const serialized = signed.serialize()

    return serialized
  }

  static async createCancelSellOfferInstructions(
    seller: PublicKey,
    auctionHouse: PublicKey,
    authority: PublicKey,
    mint: PublicKey,
    salePrice: number,
    tokenSize: number
  ): Promise<TransactionInstruction[]> {
    const [[tokenAccount], [auctionHouseFeeAccount]] = await Promise.all([
      TokenProgram.findAssociatedTokenAccountAddress(mint, seller),
      AuctionHouseProgram.findAuctionHouseFeeAddress(auctionHouse),
    ])

    const [sellerTradeState] = await AuctionHouseProgram.findTradeStateAddress(
      seller,
      auctionHouse,
      tokenAccount,
      NATIVE_MINT,
      mint,
      salePrice,
      tokenSize
    )

    const [listingReceipt] = await AuctionHouseProgram.findListingReceiptAddress(sellerTradeState)

    const accounts = {
      wallet: seller,
      tokenAccount,
      tokenMint: mint,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
      tradeState: sellerTradeState,
    }

    const args = {
      buyerPrice: salePrice,
      tokenSize,
    }

    const listingReceiptAccounts = {
      receipt: listingReceipt,
      instruction: SYSVAR_INSTRUCTIONS_PUBKEY,
    }

    const cancelIx = AuctionHouseProgram.instructions.createCancelInstruction(accounts, args)
    // cancelIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
    const cancelReceiptIx =
      AuctionHouseProgram.instructions.createCancelListingReceiptInstruction(listingReceiptAccounts)
    // cancelReceiptIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })

    return [cancelIx, cancelReceiptIx]
  }

  /**
   * Cancel sell offer and listing receipt
   *
   * @param provider     Anchor provider
   * @param seller       Seller
   * @param auctionHouse Auction house
   * @param salePrice    Sale price for tokenSize tokens
   * @param tokenSize    Number of tokens to offer for sale
   *
   * @return Tx hash
   */
  static async cancelSellOffer(
    provider: Provider,
    seller: PublicKey,
    auctionHouse: PublicKey,
    authority: PublicKey,
    mint: PublicKey,
    salePrice: number,
    tokenSize: number
  ): Promise<{ buffer: Buffer; fee: number }> {
    const instructions = await this.createCancelSellOfferInstructions(
      seller,
      auctionHouse,
      authority,
      mint,
      salePrice,
      tokenSize
    )

    const transaction = new Transaction({ feePayer: seller })
    instructions.forEach((i) => transaction.add(i))

    const result: Query_Root = (
      await indexerClient.query({
        query: LISTING_STATUS_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
          address: seller.toString(),
          mint: mint.toString(),
        },
      })
    ).data

    // let delegatedAmount = 0
    let delegate: PublicKey | undefined = undefined
    let amount = 0
    let numListings = 0

    const tokenAcct =
      result.tokenAccount && result.tokenAccount.length > 0 ? result.tokenAccount[0] : undefined
    if (tokenAcct) {
      // delegatedAmount = tokenAcct.delegatedAmount
      delegate = tokenAcct.delegate ? new PublicKey(tokenAcct.delegate) : undefined
      amount = tokenAcct.amount
      numListings =
        tokenAcct.mintObject?.editionMetadata?.metadataObject?.listingReceiptsAggregate.aggregate
          ?.count || 0
    }

    const newDelegatedAmount = Math.min(amount, numListings - 1)

    if (delegate) {
      // approve instruction for minimum of amount and numListings - 1
      const [tokenAccount] = await this.findAssociatedTokenAccountAddress(mint, seller)
      if (newDelegatedAmount > 0) {
        const approveInstruction = createApproveInstruction(
          tokenAccount,
          delegate,
          seller,
          newDelegatedAmount
        )
        transaction.add(approveInstruction)
        console.log(`APPROVING FOR DELEGATED AMOUNT ${newDelegatedAmount}`)
      } else {
        const revokeInstruction = createRevokeInstruction(tokenAccount, seller)
        transaction.add(revokeInstruction)
        console.log(`REVOKING FOR DELEGATED AMOUNT 0`)
      }
    }

    transaction.recentBlockhash = (await provider.connection.getLatestBlockhash()).blockhash

    // const encoded = Base64.fromUint8Array(
    //   transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
    //   false
    // )
    // const adminSignedIx = await txSigner(encoded) // TODO: secondaryTxSigner

    // const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

    const signed = await provider.wallet.signTransaction(transaction)

    const serialized = signed.serialize()

    const fee = await transaction.getEstimatedFee(provider.connection)

    return { buffer: serialized, fee }
  }

  /**
   * Cancel buy offer and bid receipt
   *
   * @param provider     Anchor provider
   * @param buyer        Buyer
   * @param seller       Seller
   * @param auctionHouse Auction house
   * @param salePrice    Sale price for tokenSize tokens
   * @param tokenSize    Number of tokens to offer for sale
   *
   * @return Address of listing receipt, address of seller token account
   */
  static async cancelBuyOffer(
    provider: Provider,
    buyer: PublicKey,
    tokenAccount: PublicKey,
    auctionHouse: PublicKey,
    authority: PublicKey,
    mint: PublicKey,
    salePrice: number,
    tokenSize: number,
    publicBuy = false
  ): Promise<{ buffer: Buffer; fee: number }> {
    const [auctionHouseFeeAccount] = await AuctionHouseProgram.findAuctionHouseFeeAddress(
      auctionHouse
    )

    const tradeStatePromise = publicBuy
      ? AuctionHouseProgram.findPublicBidTradeStateAddress(
          buyer,
          auctionHouse,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        )
      : AuctionHouseProgram.findTradeStateAddress(
          buyer,
          auctionHouse,
          tokenAccount,
          NATIVE_MINT,
          mint,
          salePrice,
          tokenSize
        )

    const [[buyerTradeState], [escrowPaymentAccount, escrowPaymentBump]] = await Promise.all([
      tradeStatePromise,
      AuctionHouseProgram.findEscrowPaymentAccountAddress(auctionHouse, buyer),
    ])

    const [bidReceipt] = await AuctionHouseProgram.findBidReceiptAddress(buyerTradeState)

    const accounts = {
      wallet: buyer,
      tokenAccount,
      tokenMint: mint,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
      tradeState: buyerTradeState,
    }

    const args = {
      buyerPrice: salePrice,
      tokenSize,
    }

    const transaction = new Transaction({ feePayer: buyer })
    const cancelIx = AuctionHouseProgram.instructions.createCancelInstruction(accounts, args)
    // cancelIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
    transaction.add(cancelIx)

    const bidReceiptAccounts = {
      receipt: bidReceipt,
      instruction: SYSVAR_INSTRUCTIONS_PUBKEY,
    }

    transaction.add(
      AuctionHouseProgram.instructions.createCancelBidReceiptInstruction(bidReceiptAccounts)
    )

    const withdrawAccounts = {
      wallet: buyer,
      receiptAccount: buyer,
      escrowPaymentAccount,
      treasuryMint: NATIVE_MINT,
      authority,
      auctionHouse,
      auctionHouseFeeAccount,
    }

    const withdrawArgs = {
      escrowPaymentBump,
      amount: salePrice,
    }

    const withdrawIx = AuctionHouseProgram.instructions.createWithdrawInstruction(
      withdrawAccounts,
      withdrawArgs
    )
    // withdrawIx.keys.push({ pubkey: authority, isSigner: true, isWritable: false })
    transaction.add(withdrawIx)

    transaction.recentBlockhash = (await provider.connection.getLatestBlockhash()).blockhash

    // const encoded = Base64.fromUint8Array(
    //   transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
    //   false
    // )
    // const adminSignedIx = await txSigner(encoded) // TODO: secondaryTxSigner

    // const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

    const signed = await provider.wallet.signTransaction(transaction)

    const serialized = signed.serialize()

    const fee = await transaction.getEstimatedFee(provider.connection)

    return { buffer: serialized, fee }
  }

  static async findAssociatedTokenAccountAddress(
    mint: PublicKey,
    wallet: PublicKey
  ): Promise<[PublicKey, number]> {
    return await PublicKey.findProgramAddress(
      [wallet.toBuffer(), this.TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
      this.SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID
    )
  }

  static async findPlatformSettingsAddress(): Promise<[PublicKey, number]> {
    return await PublicKey.findProgramAddress(
      [Buffer.from(this.PLATFORM_SETTINGS_PREFIX)],
      this.PUBKEY
    )
  }

  static async findMintAuthorityAddress(): Promise<[PublicKey, number]> {
    return await PublicKey.findProgramAddress(
      [Buffer.from(this.MINT_AUTHORITY_PREFIX)],
      this.PUBKEY
    )
  }

  static async findMetadataAuthorityAddress(): Promise<[PublicKey, number]> {
    return await PublicKey.findProgramAddress(
      [Buffer.from(this.METADATA_AUTHORITY_PREFIX)],
      this.PUBKEY
    )
  }

  static async findMetadataAddress(mint: PublicKey): Promise<[PublicKey, number]> {
    return await PublicKey.findProgramAddress(
      [
        Buffer.from(this.METADATA_PREFIX),
        this.TOKEN_METADATA_PROGRAM_ID.toBuffer(),
        mint.toBuffer(),
      ],
      this.TOKEN_METADATA_PROGRAM_ID
    )
  }

  static async findEditionMetadataAddress(mint: PublicKey): Promise<[PublicKey, number]> {
    return await PublicKey.findProgramAddress(
      [Buffer.from(this.EDITION_METADATA_PREFIX), mint.toBuffer()],
      this.PUBKEY
    )
  }

  static async findPrimaryReceiptAddress(
    payer: PublicKey,
    mint: PublicKey,
    primary_sale_count: number
  ): Promise<[PublicKey, number]> {
    const buf = Buffer.alloc(4)
    buf.writeUInt32LE(primary_sale_count, 0)

    return await PublicKey.findProgramAddress(
      [Buffer.from(this.PRIMARY_SALE_RECEIPT_PREFIX), payer.toBuffer(), mint.toBuffer(), buf],
      this.PUBKEY
    )
  }
}
